/**
 * 会话列表搜索/过滤口径（14.37；纯函数，可单测）。
 *
 * **全程本地零 HTTP**：会话 store 本就在内存（conversationsState 快照+推送），
 * 过滤只是一次 filter，不存在「搜索接口」——服务端也没有会话搜索接口，
 * 「搜索加人」是另一条路（ContactSearchPage，14.19b）。消息全文搜索后端没有，
 * 旧版也从未实现（legacy §6.6），这里不做。
 *
 * 匹配字段（旧版 _desktopVisibleChats 同口径，legacy §6.6）：
 * - 显示名（friendNote || nickname，conversationRowView 同口径）；
 * - 最后一条消息文本；
 * - 会话数字 ID（key 的数字段：`dm:101`→'101'、`group:202`→'202'，
 *   旧版 displayId 的等价物——新版会话记录里没有单独的 displayId 字段）。
 *
 * 刻意偏差（都比旧版少做，理由在此，下轮不要当遗漏补）：
 * - **加密会话不匹配最后一条消息**：remote.isEncrypted≠0 时 lastMsg 是密文，
 *   行上显示的也是「[加密消息]」，拿密文去 contains 只会命中乱码（行为不可解释）。
 * - **不匹配草稿文本**：草稿是自己没发出去的输入，不是会话内容；旧版 decorated
 *   chat 顺带匹配到了，这里不跟。
 * - **chips 不带数字徽标**：旧版每个 chip 的徽标都要把全列表复制一遍求和
 *   （legacy §7 性能问题之一），收益不抵成本。
 */
import type { ConversationRecord } from '~/worker/contract';

export type ConversationFilterId = 'all' | 'personal' | 'groups' | 'unread';

export const CONVERSATION_FILTERS: ReadonlyArray<{
  readonly id: ConversationFilterId;
  readonly label: string;
}> = [
  { id: 'all', label: '全部' },
  { id: 'personal', label: '私聊' },
  { id: 'groups', label: '群组' },
  { id: 'unread', label: '未读' },
];

/** 输入框原文 → 匹配用的查询串（去首尾空白 + 小写）；空串 = 不过滤 */
export function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase();
}

/** 会话 key 的数字段（旧版 displayId 等价物）；格式恒为 `dm:<n>` / `group:<n>` */
export function conversationNumericId(key: string): string {
  const at = key.indexOf(':');
  return at < 0 ? key : key.slice(at + 1);
}

/** query 必须是 normalizeQuery 的产物（已小写）；空串恒 true */
export function conversationMatchesQuery(record: ConversationRecord, query: string): boolean {
  if (query === '') return true;
  const { remote } = record;
  const name = remote.friendNote || remote.nickname;
  if (name.toLowerCase().includes(query)) return true;
  if (remote.isEncrypted === 0 && remote.lastMsg.toLowerCase().includes(query)) return true;
  return conversationNumericId(record.key).includes(query);
}

export function conversationMatchesFilter(
  record: ConversationRecord,
  filter: ConversationFilterId,
): boolean {
  switch (filter) {
    case 'personal':
      return record.key.startsWith('dm:');
    case 'groups':
      return record.key.startsWith('group:');
    case 'unread':
      return record.remote.unread > 0;
    case 'all':
      return true;
  }
}

/** 过滤态（chip≠all 或有查询串）——入口行只在非过滤态显示，空态文案也按此分叉 */
export function isFiltering(filter: ConversationFilterId, query: string): boolean {
  return filter !== 'all' || query !== '';
}

/**
 * 过滤（排序不变：入参已是 compareConversations 序，filter 保序）。
 * 非过滤态返回原引用——不白复制 2000 行（旧版每次 build 都复制，legacy §7）。
 */
export function filterConversations(
  rows: readonly ConversationRecord[],
  filter: ConversationFilterId,
  query: string,
): readonly ConversationRecord[] {
  if (!isFiltering(filter, query)) return rows;
  return rows.filter(
    (r) => conversationMatchesFilter(r, filter) && conversationMatchesQuery(r, query),
  );
}
